<template>
	<teleport to="#vue-popup">
		<div
			v-if="props.opened"
			class="fixed top-0 left-0 z-20 flex justify-center overflow-y-auto w-full h-full bg-transparentSecondary"
			@keydown.esc.prevent="closeModal"
		>
			<div
				class="bg-white sm:w-11/12 my-auto relative"
				:class="{
					'w-3/4 max-w-200': props.size === 'small',
					'w-3/5 max-w-[95rem]': props.size === 'medium',
					'w-3/4 max-w-[110rem]': props.size === 'large',
					'w-fit max-w-[110rem]': props.size === 'wrap',
					'w-full max-w-[140rem]': props.size === 'fluid-extra-large',
				}"
			>
				<header
					class="border-solid border-0"
					:class="{
						'flex justify-between items-center border-b border-lightGrey py-2.5 px-5': !props.headerless,
						'absolute h-0 overflow-visible top-2.5 right-5 z-50': props.headerless
					}"
				>
					<section class="flex flex-1 justify-center">
						<h3 class="m-0">
							{{ props.title }}
						</h3>
					</section>
					<button-circle
						size="medium"
						background-class="bg-white"
						@click="closeModal"
					>
						<icon
							which="cross"
							class="w-3 h-3 m-auto"
						/>
					</button-circle>
				</header>

				<slot />
			</div>
		</div>
	</teleport>
</template>

<script setup lang="ts">

type Props = {
	opened: boolean;
	size?: 'small' | 'medium' | 'large' | 'fluid-extra-large' | 'wrap';
	title: string | null;
	headerless?: boolean;
	beforeClose?: () => Promise<boolean>;
};

const props = withDefaults(defineProps<Props>(), {
	size: 'large',
	beforeClose: undefined,
});

const emit = defineEmits<{
	'update:opened': [ value: boolean ];
}>();

const bodyClassEnabled = useBodyClass('modal-dialog-opened');

onMounted(handleDialogVisibility);
watch(() => props.opened, handleDialogVisibility);

function handleDialogVisibility () {
	if (props.opened) {
		bodyClassEnabled.value = true;
	}
	else {
		bodyClassEnabled.value = false;
	}
}

async function closeModal () {
	const closingResult = await props.beforeClose?.();

	if (closingResult === undefined || closingResult) {
		emit('update:opened', false);
	}
}

</script>

<style lang="scss">
.modal-dialog-opened {
	overflow: hidden;
	scrollbar-gutter: stable;
}
</style>
